import { ReactNode } from "react";
import type { LinkProps } from "react-router";
import { FiChevronRight } from "react-icons/fi";
import { PiHandsClapping } from "react-icons/pi";
import { PillButton } from "~/components/PillButton";
import { SectionHeading } from "~/components/SectionHeading";
import { Text } from "~/components/Text";

export type BlurredTeaserRow = {
  key: string;
  avatarUrl: string;
  name: string;
  action: string;
};

type BlurredTeaserProps = {
  heading: string;
  rows: BlurredTeaserRow[];
  overlayTitle: string;
  overlayDescription: ReactNode;
  ctaLabel: string;
  /** When set, the CTA navigates to this destination instead of firing onCtaClick. */
  ctaTo?: LinkProps["to"];
  ctaDisabled?: boolean;
  onCtaClick?: () => void;
  className?: string;
};

export function BlurredTeaser({
  heading,
  rows,
  overlayTitle,
  overlayDescription,
  ctaLabel,
  ctaTo,
  ctaDisabled = false,
  onCtaClick,
  className = "",
}: BlurredTeaserProps) {
  return (
    <section className={`flex flex-col gap-4 ${className}`}>
      <SectionHeading>{heading}</SectionHeading>

      <div className="relative">
        <ul className="flex flex-col gap-3 blur-xs pt-3 pb-3" aria-hidden>
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-3 px-3">
              <img
                src={row.avatarUrl}
                alt=""
                className="h-11 w-11 flex-shrink-0 rounded-lg bg-butter-cream object-cover"
              />
              <div className="min-w-0 flex-1">
                <Text as="span" variant="secondary" size="md">
                  <span className="font-bold">{row.name}</span>{" "}
                  <span>{row.action}</span>
                </Text>
              </div>
              <PiHandsClapping
                size={16}
                className="flex-shrink-0 text-butter-green"
                aria-hidden
              />
              <Text
                as="span"
                variant="secondary"
                size="md"
                className="flex-shrink-0"
              >
                Reply
              </Text>
            </li>
          ))}
        </ul>

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-[18px] rounded-md bg-gradient-to-b from-white/20 to-white to-[70%] px-6 backdrop-blur-[5px]">
          <div className="flex w-full flex-col items-center gap-1.5 text-center">
            <Text variant="secondary" size="md" weight="bold">
              {overlayTitle}
            </Text>
            <Text variant="secondary" size="sm">
              {overlayDescription}
            </Text>
          </div>
          {ctaTo && !ctaDisabled ? (
            <PillButton
              to={ctaTo}
              variant="green-soft"
              size="sm"
              rightIcon={<FiChevronRight size={16} aria-hidden />}
              className="!bg-butter-cream-light !text-butter-pink !text-sm !px-[19px] !py-[7px] font-normal"
            >
              {ctaLabel}
            </PillButton>
          ) : (
            <PillButton
              type="button"
              onClick={onCtaClick}
              disabled={ctaDisabled}
              variant="green-soft"
              size="sm"
              rightIcon={<FiChevronRight size={16} aria-hidden />}
              className="!bg-butter-cream-light !text-butter-pink !text-sm !px-[19px] !py-[7px] font-normal disabled:opacity-50 disabled:cursor-default disabled:hover:scale-100"
            >
              {ctaLabel}
            </PillButton>
          )}
        </div>
      </div>
    </section>
  );
}
